import React, { useState, type FC, useEffect, type ReactNode } from 'react';
import { Select, Space, Button, Switchbox } from '@g2u/ui-kit';
import { addDays, format, isBefore, isValid, parse } from 'date-fns';
import { Alert, Col, Divider, type FormInstance, Row, Popconfirm, TreeSelect, Form } from 'antd';
import { isArray, isEqual } from 'lodash';
import { presetPalettes } from '@ant-design/colors';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  EditOutlined,
  PhoneOutlined,
  LikeFilled,
  PhoneFilled,
  InfoCircleFilled,
  WarningFilled,
} from '@ant-design/icons';
import Big from 'big.js';

import { getJobSearchWithFilters, getSkillSets, getSuburb, getTechnicianList, postVoipCall } from 'queries';
import { AddressLookup, AvailabilityCalendar, CustomerUpdateModal, LostJobModal, Section } from 'components';
import { Toast, formatTimeToAmPm, formatToFullDate, getServiceFormat, isTimeExpired, parseErrors } from 'helpers';
import {
  AddressType,
  ServiceType,
  type JsonDataItem,
  type WithOptionalDataTest,
  SlotAvailability,
  type JsonData,
  AvailabilityType,
  type TimeSlot,
  FullAvailabilityStatus,
} from 'types';
import { useAppointmentDuration, useGetAppointments } from './hooks';
import styles from './appointment-selection.module.scss';

export namespace AppointmentSelection {
  export type Props = {
    isRescheduling?: boolean;
    serviceId?: string;
    form: FormInstance;
    isConfirmed: boolean;
    onConfirm: (status: boolean) => void;
    changedFields?: JsonDataItem;
    timezone: string;
    onTodaySlotsLoaded?: (todaySlots?: JsonDataItem | null) => void;
    onRemoteSlotsChecked?: (isAvaialble: boolean) => void;
    children?: ReactNode;
    isNewCustomer: boolean;
    customer: JsonDataItem | null;
    location: JsonDataItem;
    setLocation: (location: JsonDataItem) => void;
    setTechnician: (record: JsonDataItem | null) => void;
    technician: JsonDataItem | null;
    setSlot: (record: TimeSlot | null) => void;
    slot: TimeSlot | null;
    job: JsonDataItem | null;
    serviceSelection: JsonDataItem | null;
    setServiceType: (type: ServiceType) => void;
    lostJob: JsonDataItem | null;
    setLostJob: (lostJob: JsonDataItem | null) => void;
    resetBookingProgress: () => void;
    setPreviousJob: (job: JsonDataItem) => void;
    setTechniciansList: (list: JsonData) => void;
    previousJob: JsonDataItem | null;
    productList: JsonData | null;
    productSelection: JsonDataItem | null;
    serviceType: ServiceType;
    isAvailabilityOverrideEnabled: boolean;
    onAvailabilityOverrideChange: (isEnabled: boolean) => void;
  } & WithOptionalDataTest;

  export const $: FC<Props> = (props) => {
    const {
      isNewCustomer,
      customer,
      location,
      setLocation,
      setTechnician,
      technician,
      setSlot,
      slot,
      job,
      serviceSelection,
      setServiceType,
      lostJob,
      setLostJob,
      resetBookingProgress,
      setPreviousJob,
      setTechniciansList,
      previousJob,
      productList,
      productSelection,
      serviceType,
    } = props;
    const {
      isRescheduling,
      serviceId,
      form,
      changedFields,
      isConfirmed,
      onConfirm,
      timezone,
      onTodaySlotsLoaded,
      onRemoteSlotsChecked,
      isAvailabilityOverrideEnabled,
      onAvailabilityOverrideChange,
    } = props;
    const isOverrideModeVisible =
      !form.getFieldValue('nominatedTech')?.includes('_parent') && !!form.getFieldValue('nominatedTech');
    const isOverrideSlotSelected = Object.values(FullAvailabilityStatus)
      .filter(Boolean)
      .includes(slot?.availability as unknown as FullAvailabilityStatus);

    const isGeekCoverJob = customer?.is_geek_cover || job?.service_type_ID === 3;
    const jobTypeInitialValue = job?.service_type_ID
      ? job?.service_type_ID.toString() // if rescheduling
      : serviceType || ServiceType.OnSite;
    const [technicianList, setTechnicianList] = useState<JsonData>([]);
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const [serviceSuburb, setServiceSuburb] = useState<string>(customer?.address.suburb || location.suburb);
    const [servicePostcode, setServicePostcode] = useState<string>(customer?.address.postcode || location.postcode);
    const [selectedSlot, setSelectedSlot] = useState<JsonDataItem | null>(slot);
    const [appointmentSuburbId, setAppointmentSuburbId] = useState<number>(customer?.address.suburb_ID || location.ID);
    const requireConfirmation =
      !isConfirmed &&
      !!slot &&
      (slot.availability === SlotAvailability.Confirmation ||
        isOverrideSlotSelected ||
        isTimeExpired(`${format(parse(slot.day, 'dd-MM-yyyy', new Date()), 'yyyy-MM-dd')} ${slot.time}`, timezone, 2) ||
        (slot.isSurrounding && serviceType === ServiceType.OnSite));
    const [showLostJobModal, setShowLostJobModal] = useState<boolean>(false);
    const [timeSlotPeriod, setTimeSlotPeriod] = useState<AvailabilityCalendar.TimePeriods>(
      AvailabilityCalendar.TimePeriods.All,
    );

    const navigate = useNavigate();
    const appointmentDuration = useAppointmentDuration(job, productList, productSelection);
    const { slots, remoteTodaySlots, isSlotsLoading, fetchAppointments } = useGetAppointments(
      form,
      appointmentDuration,
      customer?.ID,
      isRescheduling,
      serviceId,
      serviceSelection,
      appointmentSuburbId,
      lostJob?.searchHistoryID,
      changedFields,
      serviceSelection?.ID,
      isAvailabilityOverrideEnabled,
    );

    // get tech list
    const { mutate: getTechList } = useMutation(getTechnicianList, {
      onSuccess: (data: JsonData) => {
        setTechniciansList(data);
        setTechnicianList(data.map((item) => ({ ...item, value: `${item.value}_parent` }))); // TreeSelect require different values for parent and children
      },
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    });

    const { mutate: getPreviousJob } = useMutation(getJobSearchWithFilters, {
      onSuccess: (data: JsonDataItem) => {
        setPreviousJob({ ...data.data[0], active: false });
      },
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    });

    // get skillsets
    const { data: skillSets } = useQuery(['get-skillsets'], getSkillSets, {
      onError: (data: JsonDataItem) => {
        Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
      },
    });

    // Suburb lookup: get list
    const { mutate: suburbLookup, data: suggestedSuburbs } = useMutation(getSuburb, {
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    });

    // Technician call
    const { mutate: callTechnician, isLoading: isCalling } = useMutation(postVoipCall, {
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    });

    const handleNextSlide = () => {
      // If no technicians in area
      if (isArray(!slots?.appointments)) {
        return null;
      }

      const date = parse(Object.keys(slots?.appointments).slice(-1)[0], 'dd-MM-yyyy', new Date());

      if (!isValid(date) || isBefore(date, new Date()) || !slots) {
        return null;
      }
      // prevent extra calls until all days loaded
      Object.keys(slots?.appointments || {}).length && fetchAppointments(addDays(date, 1), 1);

      return;
    };

    useEffect(() => {
      customer &&
        getPreviousJob({
          customerID: customer.ID,
          page: 1,
          recordsPerPage: 500,
        });
      // TODO: ID 789630 JOB
      // reset selection if config changed
      setSlot(null);
      setTechnician(null);

      if (changedFields?.jobType) {
        setServiceType(changedFields.jobType);
      }

      // update tech list for nomination
      if (changedFields?.jobType || changedFields?.skillsets || changedFields?.serviceLocationSuburb) {
        form.setFieldValue('nominatedTech', '');
        getTechList({
          serviceType: form.getFieldValue('jobType'),
          skillIds: form.getFieldValue('skillsets') || serviceSelection?.product_selection_raw.skillsets,
          suburbId: customer?.address.suburb_ID || location.ID || job?.customer?.address.suburb_ID,
          customerId: customer?.ID || job?.customer?.ID,
        });
      }

      // client side filtering
      if (changedFields?.timePeriod) {
        setTimeSlotPeriod(changedFields?.timePeriod);
      }
      // save last selection to store
      if (changedFields?.jobType) {
        setServiceType(changedFields.jobType);
      }
    }, [changedFields, appointmentSuburbId]);

    useEffect(() => {
      onAvailabilityOverrideChange(false);
    }, [isOverrideModeVisible]);

    useEffect(() => {
      onRemoteSlotsChecked?.(Boolean(remoteTodaySlots));
    }, [remoteTodaySlots]);

    useEffect(() => {
      onTodaySlotsLoaded?.(slots);
    }, [slots]);

    useEffect(() => {
      setSelectedSlot(slot);
      !isEqual(slot, selectedSlot) &&
        onConfirm(
          slot?.availability !== SlotAvailability.Confirmation &&
            (!slot?.isSurrounding || serviceType !== ServiceType.OnSite) &&
            !!slot &&
            !isTimeExpired(
              `${format(parse(slot.day, 'dd-MM-yyyy', new Date()), 'yyyy-MM-dd')} ${slot.time}`,
              timezone,
              2,
            ) &&
            !isOverrideSlotSelected,
        );
    }, [slot]);

    useEffect(() => {
      if (previousJob?.active) {
        form.setFieldValue('nominatedTech', previousJob?.technician?.ID.toString());
        fetchAppointments(form.getFieldValue('jumpToDate'), 5, true);
      }
    }, [previousJob?.active]);

    useEffect(() => {
      // reset store
      setSlot(null);
      setTechnician(null);
      getTechList({
        serviceType: form.getFieldValue('jobType'),
        skillIds: form.getFieldValue('skillsets') || serviceSelection?.product_selection_raw.skillsets,
        suburbId: customer?.address.suburb_ID || location.ID || job?.customer?.address.suburb_ID,
        customerId: customer?.ID || job?.customer?.ID,
      });
    }, []);

    useEffect(() => {
      // reset current slot selection
      isSlotsLoading && setSlot(null);
    }, [isSlotsLoading]);

    useEffect(() => {
      serviceSelection && form.setFieldValue('skillsets', serviceSelection?.product_selection_raw.skillsets);
    }, [serviceSelection]);

    return (
      <>
        <Section.$ full>
          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
            {!isRescheduling && (
              <Row gutter={[24, 24]} wrap className={styles.appointmentSelection__suburb}>
                <Col span={24}>
                  <Space.$ compact fullWidth direction={Space.Direction.Horizontal} size={Space.Size.ExtraSmall}>
                    <Button.$ dataTestId="placeholder" className={styles.appointmentSelection__suburbPrefix}>
                      Suburb
                    </Button.$>

                    <AddressLookup.$
                      name="serviceLocationSuburb"
                      placeholder={'Enter Postal Code'}
                      onAddressSearch={(value: string) => {
                        suburbLookup({ value, type: AddressType.Physical });
                      }}
                      onAddressSelect={(option: JsonDataItem) => {
                        setServiceSuburb(option.data.suburb);
                        setServicePostcode(option.data.postcode);
                        isNewCustomer && setLocation(option.data);
                        setAppointmentSuburbId(option.data.ID);
                      }}
                      initialValue={`${serviceSuburb}, ${servicePostcode}`}
                      addressOptions={suggestedSuburbs?.map((item: JsonDataItem) => {
                        return {
                          label: `${item.suburb}, ${item.postcode}`,
                          value: `${item.suburb}, ${item.postcode}`,
                          data: item,
                        };
                      })}
                      className={styles.appointmentSelection__suburbSelect}
                      dataTestId="home-suburb"
                      readonly={!isNewCustomer || isRescheduling || isSlotsLoading}
                    />

                    {!isNewCustomer && (
                      <Button.$
                        disabled={isRescheduling || isSlotsLoading}
                        dataTestId="get-slots"
                        onClick={() => {
                          setShowUpdateModal(true);
                        }}
                      >
                        <EditOutlined />
                      </Button.$>
                    )}
                  </Space.$>
                </Col>
              </Row>
            )}
            <Row gutter={[24, 24]} wrap className={styles.appointmentSelection__job}>
              <Col xl={12} sm={24}>
                <Form.Item
                  label="Nominate Technician"
                  name="nominatedTech"
                  initialValue={job?.products[0]?.service_technicians[0]?.technician_ID.toString() || ''}
                  colon
                  labelCol={{ span: 9 }}
                  wrapperCol={{ span: 15 }}
                >
                  <TreeSelect
                    disabled={isSlotsLoading}
                    showSearch
                    filterTreeNode={(inputValue: string, treeNode: JsonDataItem) =>
                      treeNode.title.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    treeData={[{ title: 'Any Technician', value: '' }, ...technicianList]}
                  />
                </Form.Item>
              </Col>
              <Col xl={12} sm={24}>
                <Select.$
                  disabled={isRescheduling || isSlotsLoading || isAvailabilityOverrideEnabled}
                  name={'jobType'}
                  label="On-site / Remote"
                  colon
                  labelCol={{ span: 9 }}
                  wrapperCol={{ span: 15 }}
                  fullWidth
                  initialValue={jobTypeInitialValue}
                  options={[
                    {
                      label: 'On-site',
                      value: ServiceType.OnSite,
                    },
                    {
                      label: 'Remote',
                      value: isGeekCoverJob ? ServiceType.GeekCover : ServiceType.Remote,
                      disabled: getServiceFormat(productSelection?.products, productList) === AvailabilityType.OnSite,
                    },
                  ]}
                />
              </Col>
              <Col xl={12} sm={24}>
                <Select.$
                  name={'timePeriod'}
                  label="Time Period"
                  colon
                  labelCol={{ span: 9 }}
                  wrapperCol={{ span: 15 }}
                  fullWidth
                  disabled={isAvailabilityOverrideEnabled}
                  initialValue={AvailabilityCalendar.TimePeriods.All}
                  options={[
                    { label: AvailabilityCalendar.TimePeriods.All, value: AvailabilityCalendar.TimePeriods.All },
                    {
                      label: AvailabilityCalendar.TimePeriods.Morning,
                      value: AvailabilityCalendar.TimePeriods.Morning,
                    },
                    {
                      label: AvailabilityCalendar.TimePeriods.Afternoon,
                      value: AvailabilityCalendar.TimePeriods.Afternoon,
                    },
                    {
                      label: AvailabilityCalendar.TimePeriods.Evening,
                      value: AvailabilityCalendar.TimePeriods.Evening,
                    },
                  ]}
                />
              </Col>

              {!isRescheduling && appointmentDuration && (
                <Col xl={12} sm={24}>
                  <Select.$
                    name={'booking-hours'}
                    label="Booking Hour(s)"
                    colon
                    labelCol={{ span: 9 }}
                    wrapperCol={{ span: 15 }}
                    fullWidth
                    disabled
                    initialValue={appointmentDuration.toString()}
                    options={[
                      {
                        label: `${Big(appointmentDuration).div(60).round(1).toString() + ' Hour(s)'} `,
                        value: appointmentDuration.toString(),
                      },
                    ]}
                  />
                </Col>
              )}
              {skillSets && (
                <Col xl={12} sm={24}>
                  <Select.$
                    disabled={isAvailabilityOverrideEnabled}
                    name={'skillsets'}
                    label="Skillset"
                    colon
                    labelCol={{ span: 9 }}
                    wrapperCol={{ span: 15 }}
                    fullWidth
                    initialValue={serviceSelection?.product_selection_raw.skillsets}
                    mode="multiple"
                    options={[
                      { label: 'Any skills', value: '' },
                      ...(skillSets ?? [])
                        .sort((a: any, b: any) => a?.ID - b?.ID)
                        .map((skill: JsonDataItem) => ({
                          label: skill?.description,
                          value: skill?.ID?.toString(),
                        })),
                    ]}
                  />
                </Col>
              )}
              <Col xl={12} sm={24}>
                <Switchbox.$
                  disabled={isSlotsLoading}
                  initialValue={false}
                  name={'surroundingGeeks'}
                  label="Include Surrounding-area Techs"
                />
              </Col>
            </Row>
            {props.children}
            <Divider />
            <Space.$ size={Space.Size.Large} direction={Space.Direction.Vertical}>
              <AvailabilityCalendar.$
                onReport={
                  lostJob?.searchHistoryID
                    ? () => {
                        setShowLostJobModal(true);
                      }
                    : undefined
                }
                dayList={slots}
                dayPeriod={timeSlotPeriod}
                loading={isSlotsLoading}
                onReachEnd={handleNextSlide}
                slot={slot}
                timezone={timezone}
                technician={technician}
                onSlotSelected={setSlot}
                onTechnicianSelected={setTechnician}
                skillSet={serviceSelection?.product_selection_raw.skillsets}
                isAvailabilityOverrideEnabled={isAvailabilityOverrideEnabled}
                hasAvailabilityOverrideToggle={isOverrideModeVisible}
                onAvailabilityOverrideChange={onAvailabilityOverrideChange}
              />
            </Space.$>
          </Space.$>
        </Section.$>
        <br />
        <Space.$ direction={Space.Direction.Horizontal} size={Space.Size.Middle}>
          {!isAvailabilityOverrideEnabled && (
            <Space.$ direction={Space.Direction.Horizontal} size={Space.Size.Small} align={Space.Align.Center}>
              <LikeFilled style={{ fontSize: 20, color: presetPalettes.red[5] }} /> The Best Tech
            </Space.$>
          )}
          <Space.$ direction={Space.Direction.Horizontal} size={Space.Size.Small} align={Space.Align.Center}>
            <PhoneFilled style={{ fontSize: 20, color: presetPalettes.orange[5] }} />
            Call to Confirm
          </Space.$>
          <Space.$ direction={Space.Direction.Horizontal} size={Space.Size.Small} align={Space.Align.Center}>
            <InfoCircleFilled className={styles.appointmentSelection__infoIcon} style={{ fontSize: 20 }} />
            Has Special Notes
          </Space.$>
          <Space.$ direction={Space.Direction.Horizontal} size={Space.Size.Small} align={Space.Align.Center}>
            <WarningFilled style={{ fontSize: 20, color: presetPalettes.orange[5] }} />
            In a Different Time Zone
          </Space.$>
        </Space.$>
        {technician && slot && (
          <>
            <br />
            <Space.$ size={Space.Size.Small} direction={Space.Direction.Vertical} fullWidth>
              {requireConfirmation && slot.availability === FullAvailabilityStatus.Unavailable && (
                <Alert
                  message={
                    <>
                      {technician?.name.split(' ')[0]} is <b>unavailable</b> at the selected time. Please call to
                      confirm.
                    </>
                  }
                  type={'warning'}
                  showIcon
                />
              )}
              <Alert
                message={
                  <>
                    {requireConfirmation && (
                      <span className={styles.appointmentSelection__confirmationWarning}>
                        <WarningFilled
                          className={styles.appointmentSelection__confirmationWarning__icon}
                          style={{ fontSize: 20, color: presetPalettes.red[5] }}
                        />
                        <b>WARNING!</b> This technician{' '}
                        <b>
                          <u>MUST BE CALLED</u>
                        </b>{' '}
                        before the booking can be confirmed.
                        <WarningFilled
                          className={styles.appointmentSelection__confirmationWarning__icon}
                          style={{ fontSize: 20, color: presetPalettes.red[5] }}
                        />
                      </span>
                    )}
                    Technician <b>{technician?.name.split(' ')[0]}</b> and arrival window{' '}
                    <b>
                      {formatTimeToAmPm(slot?.time)}-{formatTimeToAmPm(slot?.time, 1)} - {formatToFullDate(slot?.day)}
                    </b>{' '}
                    have been selected.
                  </>
                }
                type={requireConfirmation ? 'error' : 'info'}
                action={
                  <Space.$ size={Space.Size.Small}>
                    {requireConfirmation && (
                      <Button.$
                        size={Button.Size.Small}
                        dataTestId="confirm-btn"
                        onClick={() => {
                          onConfirm(true);
                        }}
                      >
                        I&apos;ve called the technician
                      </Button.$>
                    )}{' '}
                    <Popconfirm
                      title="Alert"
                      onConfirm={() => {
                        callTechnician(technician?.contact[0].replace(/\s/g, ''));
                      }}
                      description={`Do you want to call ${technician?.name.split(' ')[0]}`}
                      okText="Yes"
                    >
                      <div>
                        <Button.$
                          size={Button.Size.Small}
                          type={requireConfirmation ? Button.Type.Primary : Button.Type.Default}
                          danger={requireConfirmation}
                          onClick={() => null}
                          dataTestId="call"
                          loading={isCalling}
                        >
                          <PhoneOutlined /> Call technician
                        </Button.$>
                      </div>
                    </Popconfirm>
                  </Space.$>
                }
              />
            </Space.$>
          </>
        )}
        <CustomerUpdateModal.$
          modalTitle="Update Customer File Details"
          okText="Update"
          isOpen={showUpdateModal}
          onOk={(data: JsonDataItem | null) => {
            if (data) {
              form.setFieldValue('serviceLocationSuburb', `${data.address.suburb}, ${data.address.postcode}`);
              setAppointmentSuburbId(data.address.suburb_ID);
            }
            setShowUpdateModal(false);
          }}
          onCancel={() => {
            setShowUpdateModal(false);
          }}
        />
        <LostJobModal.$
          isOpen={showLostJobModal}
          onOk={() => {
            setLostJob(null);
            setShowLostJobModal(false);
            navigate('/dashboard');
            resetBookingProgress();
          }}
        />
      </>
    );
  };
}
